import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { API } from "@/lib/api";
import { ChevronLeft, ChevronRight } from "lucide-react";
import FadeIn from "@/components/FadeIn";

const resolveImg = (u) => {
  if (!u) return null;
  if (u.startsWith("http")) return u;
  return `${API.replace(/\/api$/, "")}${u}`;
};

const MENU_BG = "https://customer-assets.emergentagent.com/job_modern-bistro-portal/artifacts/cvy0c0vy_IMG_8320.jpg";

// Curated elegant fallback photo per category (used when admin hasn't uploaded a real dish photo)
const CATEGORY_FALLBACKS = {
  antipasti_carne: [
    "https://images.unsplash.com/photo-1626200419199-391ae4be7a41?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1505253758473-96b7015fcd40?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1551183053-bf91a1d81141?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1580476262798-bddd9f4b7369?auto=format&fit=crop&w=600&q=80",
  ],
  antipasti_pesce: [
    "https://images.unsplash.com/photo-1559339352-11d035aa65de?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1559847844-5315695dadae?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1572441713132-c542fc4fe282?auto=format&fit=crop&w=600&q=80",
  ],
  antipasti_vegetariani: [
    "https://images.unsplash.com/photo-1540420773420-3366772f4999?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?auto=format&fit=crop&w=600&q=80",
  ],
  primi: [
    "https://images.unsplash.com/photo-1551183053-bf91a1d81141?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1473093295043-cdd812d0e601?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1621996346565-e3dbc646d9a9?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1572441713132-c542fc4fe282?auto=format&fit=crop&w=600&q=80",
  ],
  secondi: [
    "https://images.unsplash.com/photo-1546833999-b9f581a1996d?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1544025162-d76694265947?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1485921325833-c519f76c4927?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?auto=format&fit=crop&w=600&q=80",
  ],
  pinsa: [
    "https://images.unsplash.com/photo-1574071318508-1cdbab80d002?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1604068549290-dea0e4a305ca?auto=format&fit=crop&w=600&q=80",
  ],
  burgers: [
    "https://images.unsplash.com/photo-1568901346375-23c9450c58cd?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1571091718767-18b5b1457add?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1550317138-10000687a72b?auto=format&fit=crop&w=600&q=80",
  ],
  insalate: [
    "https://images.unsplash.com/photo-1512621776951-a57141f2eefd?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1540420773420-3366772f4999?auto=format&fit=crop&w=600&q=80",
  ],
  contorni: [
    "https://images.unsplash.com/photo-1639024471283-03518883512d?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1568051243851-f9b136146e97?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1593560708920-61dd98c46a4e?auto=format&fit=crop&w=600&q=80",
  ],
  fritti: [
    "https://images.unsplash.com/photo-1639024471283-03518883512d?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1582169296194-e4d644c48063?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1606755962773-d324e0a13086?auto=format&fit=crop&w=600&q=80",
  ],
  bambini: [
    "https://images.unsplash.com/photo-1473093295043-cdd812d0e601?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1559339352-11d035aa65de?auto=format&fit=crop&w=600&q=80",
  ],
  bibite: [
    "https://images.unsplash.com/photo-1513558161293-cdaf765ed2fd?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1581636625402-29b2a704ef13?auto=format&fit=crop&w=600&q=80",
  ],
  dolci: [
    "https://images.unsplash.com/photo-1571877227200-a0d98ea607e9?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1551024601-bec78aea704b?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1488477181946-6428a0291777?auto=format&fit=crop&w=600&q=80",
  ],
  cocktail: [
    "https://images.unsplash.com/photo-1551024709-8f23befc6f87?auto=format&fit=crop&w=600&q=80",
  ],
  vini: [
    "https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?auto=format&fit=crop&w=600&q=80",
  ],
};

const fallbackFor = (catKey, idx) => {
  const arr = CATEGORY_FALLBACKS[catKey] || CATEGORY_FALLBACKS.primi;
  return arr[idx % arr.length];
};

// Elegant flourish separator inspired by classic Italian menus
function Flourish({ className = "" }) {
  return (
    <div className={`flex items-center justify-center gap-3 ${className}`} aria-hidden>
      <span className="h-px w-8 bg-[#C9A96A]" />
      <span className="text-[#C9A96A] text-xs">❦</span>
      <span className="h-px w-8 bg-[#C9A96A]" />
    </div>
  );
}

export default function MenuPage() {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [active, setActive] = useState(null);
  const [flip, setFlip] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/menu").then((r) => setItems(r.data)).finally(() => setLoading(false));
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => setCategories([]));
  }, []);

  const visibleCategories = useMemo(() => {
    const present = new Set(items.filter((i) => i.is_available !== false).map((i) => i.category));
    return categories.filter((c) => present.has(c.key));
  }, [items, categories]);

  useEffect(() => {
    if (visibleCategories.length > 0 && !visibleCategories.some((c) => c.key === active)) {
      setActive(visibleCategories[0].key);
    }
  }, [visibleCategories, active]);

  const filtered = useMemo(
    () => items.filter((i) => i.category === active && i.is_available !== false),
    [items, active]
  );

  const changeCategory = (key) => {
    if (key === active) return;
    setFlip(true);
    setTimeout(() => {
      setActive(key);
      setFlip(false);
    }, 260);
  };

  const currentIndex = visibleCategories.findIndex((c) => c.key === active);
  const goPrev = () => currentIndex > 0 && changeCategory(visibleCategories[currentIndex - 1].key);
  const goNext = () =>
    currentIndex >= 0 &&
    currentIndex < visibleCategories.length - 1 &&
    changeCategory(visibleCategories[currentIndex + 1].key);

  const lang = i18n.language;
  const LANG_FALLBACK = { at: "de", ar: "en" };
  const src = LANG_FALLBACK[lang] || lang;
  const nameOf = (it) => it[`name_${lang}`] || it[`name_${src}`] || it.name_it;
  const descOf = (it) => it[`description_${lang}`] || it[`description_${src}`] || it.description_it;
  const labelOf = (c) => c[`label_${lang}`] || c[`label_${src}`] || c.label_it;

  const activeLabel = visibleCategories.find((c) => c.key === active);

  // Split items into two "pages" (left / right of open book) for desktop only
  const half = Math.ceil(filtered.length / 2);
  const leftPage = filtered.slice(0, half);
  const rightPage = filtered.slice(half);

  return (
    <div data-testid="menu-page" className="relative min-h-screen overflow-hidden">
      {/* Dark background */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url('${MENU_BG}')`,
          filter: "blur(24px) saturate(1.1) brightness(0.32)",
          transform: "scale(1.15)",
        }}
        aria-hidden
      />
      <div className="absolute inset-0 bg-[#0F0906]/70" aria-hidden />

      <div className="relative z-10 py-16 sm:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Elegant title */}
          <FadeIn className="text-center mb-10 sm:mb-14">
            <p className="text-[10px] tracking-[0.5em] uppercase text-[#C9A96A]">
              {t("menu.eyebrow")}
            </p>
            <h1 className="font-serif italic text-4xl sm:text-5xl lg:text-6xl tracking-wide text-[#F5EEDC] mt-3 drop-shadow-[0_2px_20px_rgba(0,0,0,0.5)]">
              {t("menu.title")}
            </h1>
            <Flourish className="mt-5" />
          </FadeIn>

          {/* Category ribbon tabs */}
          <div className="flex flex-wrap justify-center gap-x-1 gap-y-2 mb-6 px-2">
            {visibleCategories.map((cat) => (
              <button
                key={cat.key}
                data-testid={`menu-category-${cat.key}`}
                onClick={() => changeCategory(cat.key)}
                className={`px-4 py-2 text-[11px] sm:text-xs uppercase tracking-[0.2em] font-medium transition-all border ${
                  active === cat.key
                    ? "bg-[#C9A96A] text-[#0E1B15] border-[#C9A96A] shadow-lg"
                    : "text-[#C9A96A]/85 border-[#C9A96A]/25 hover:bg-[#C9A96A]/10 hover:border-[#C9A96A]/60"
                }`}
              >
                {labelOf(cat)}
              </button>
            ))}
          </div>

          {/* Book */}
          <div className="relative">
            {/* Prev / Next arrows on the sides */}
            <button
              onClick={goPrev}
              disabled={currentIndex <= 0}
              aria-label="Categoria precedente"
              data-testid="menu-prev-category"
              className="hidden md:flex absolute left-[-56px] top-1/2 -translate-y-1/2 w-11 h-11 items-center justify-center text-[#C9A96A] hover:text-[#F5EEDC] disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-7 h-7" strokeWidth={1.5} />
            </button>
            <button
              onClick={goNext}
              disabled={currentIndex >= visibleCategories.length - 1}
              aria-label="Categoria successiva"
              data-testid="menu-next-category"
              className="hidden md:flex absolute right-[-56px] top-1/2 -translate-y-1/2 w-11 h-11 items-center justify-center text-[#C9A96A] hover:text-[#F5EEDC] disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight className="w-7 h-7" strokeWidth={1.5} />
            </button>

            {/* Open book */}
            <div
              className="relative shadow-2xl menu-book"
              style={{
                perspective: "2000px",
              }}
            >
              {/* Center spine (desktop) */}
              <div
                className="hidden md:block absolute top-8 bottom-8 left-1/2 -translate-x-1/2 w-px bg-gradient-to-b from-transparent via-[#C9A96A]/40 to-transparent pointer-events-none z-10"
                aria-hidden
              />

              <div
                key={active}
                className={`grid md:grid-cols-2 gap-0 min-h-[600px] transition-all duration-300 ease-out ${
                  flip ? "opacity-0 translate-y-2 rotate-x-[3deg]" : "opacity-100 translate-y-0"
                }`}
                style={{ transformStyle: "preserve-3d" }}
              >
                {/* LEFT PAGE — Category header + first half */}
                <section
                  data-testid="menu-page-left"
                  className="relative px-6 sm:px-12 lg:px-16 py-12 sm:py-16 menu-page-left"
                >
                  {activeLabel && (
                    <FadeIn delay={80} className="mb-10">
                      <p className="text-[10px] tracking-[0.5em] uppercase text-[#C9A96A]/85">
                        Menu
                      </p>
                      <h2 className="font-serif italic text-5xl sm:text-6xl tracking-tight text-[#F5EEDC] mt-2 leading-[0.95]">
                        {labelOf(activeLabel)}
                      </h2>
                      <Flourish className="mt-5 justify-start" />
                    </FadeIn>
                  )}

                  {loading ? (
                    <div className="text-[#C9A96A]">…</div>
                  ) : filtered.length === 0 ? (
                    <p className="text-[#C9A96A]" data-testid="menu-empty">
                      {t("menu.empty")}
                    </p>
                  ) : (
                    <ul className="space-y-6 sm:space-y-8">
                      {leftPage.map((it, idx) => (
                        <MenuItem
                          key={it.id}
                          item={it}
                          idx={idx}
                          name={nameOf(it)}
                          description={descOf(it)}
                        />
                      ))}
                      {/* On mobile the right page items go here too */}
                      <li className="md:hidden">
                        <ul className="space-y-6 sm:space-y-8">
                          {rightPage.map((it, idx) => (
                            <MenuItem
                              key={it.id}
                              item={it}
                              idx={leftPage.length + idx}
                              name={nameOf(it)}
                              description={descOf(it)}
                            />
                          ))}
                        </ul>
                      </li>
                    </ul>
                  )}

                  <div className="text-center text-[10px] tracking-[0.3em] text-[#C9A96A]/50 mt-14 sm:mt-16 uppercase">
                    Harry&apos;s · Salotto &amp; Cucina
                  </div>
                </section>

                {/* RIGHT PAGE — Second half */}
                <section
                  data-testid="menu-page-right"
                  className="relative hidden md:block px-6 sm:px-12 lg:px-16 py-12 sm:py-16 menu-page-right"
                >
                  {rightPage.length === 0 ? (
                    <div className="h-full flex items-center justify-center opacity-30">
                      <Flourish />
                    </div>
                  ) : (
                    <ul className="space-y-6 sm:space-y-8">
                      {rightPage.map((it, idx) => (
                        <MenuItem
                          key={it.id}
                          item={it}
                          idx={leftPage.length + idx}
                          name={nameOf(it)}
                          description={descOf(it)}
                        />
                      ))}
                    </ul>
                  )}

                  <div className="text-center text-[10px] tracking-[0.3em] text-[#C9A96A]/50 mt-14 sm:mt-16 uppercase">
                    Vicenza
                  </div>
                </section>
              </div>
            </div>

            {/* Mobile category paginator */}
            <div className="md:hidden flex items-center justify-between mt-6 text-[#C9A96A]">
              <button
                onClick={goPrev}
                disabled={currentIndex <= 0}
                className="inline-flex items-center gap-1 text-xs uppercase tracking-widest disabled:opacity-30"
              >
                <ChevronLeft className="w-4 h-4" /> Prec.
              </button>
              <span className="text-xs tracking-widest">
                {currentIndex + 1} / {visibleCategories.length}
              </span>
              <button
                onClick={goNext}
                disabled={currentIndex >= visibleCategories.length - 1}
                className="inline-flex items-center gap-1 text-xs uppercase tracking-widest disabled:opacity-30"
              >
                Succ. <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          <p className="text-center text-[11px] text-white/50 mt-6 tracking-widest uppercase">
            {t("menu.subtitle")}
          </p>
        </div>
      </div>
    </div>
  );
}

function MenuItem({ item, idx, name, description }) {
  const img = resolveImg(item.image_url) || fallbackFor(item.category, idx);
  return (
    <FadeIn
      as="li"
      delay={Math.min(idx, 8) * 60}
      y={16}
      data-testid={`menu-item-${item.id}`}
      className="group flex gap-5 sm:gap-6 items-start"
    >
      <div className="relative w-28 h-28 sm:w-32 sm:h-32 lg:w-36 lg:h-36 flex-shrink-0 overflow-hidden bg-[#1B2A22] rounded-sm shadow-[0_10px_28px_rgba(0,0,0,0.55)]">
        <img
          src={img}
          alt={name}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-110"
        />
        <div className="absolute inset-0 ring-1 ring-[#C9A96A]/25" aria-hidden />
      </div>
      <div className="flex-1 min-w-0 pt-1">
        <h3 className="font-serif text-xl sm:text-2xl tracking-tight text-[#F5EEDC] leading-tight font-medium">
          {name}
        </h3>
        {description && (
          <p className="text-sm text-[#D5C9A8]/85 mt-2 leading-relaxed italic">
            {description}
          </p>
        )}
        <div className="mt-3 h-px bg-gradient-to-r from-[#C9A96A]/40 via-[#C9A96A]/15 to-transparent" />
      </div>
    </FadeIn>
  );
}
